

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Card,
    Grid,
    Skeleton,
    Snackbar,
    Alert,
    IconButton,
    Tooltip
} from "@mui/material";
import { Edit, Delete, Save, Close } from '@mui/icons-material';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import { useAuth } from 'auth/Auth';
import InlineEditField from 'components/CustonEditableInlineField/CustomEditableInlineField';
import { formatDate } from 'utils/DateTimeConversion';
import { commonIconButtonStyles, commonIconStyles } from 'assets/theme/components/iconButtonStyles';
import { UiDetailSection } from 'components/v2/record/DetailSection';

const TravelContactDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { axiosInstance } = useAuth();
    const [contact, setContact] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [updatedFields, setUpdatedFields] = useState({});
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    useEffect(() => {
        fetchContactData();
    }, [id]);

    const fetchContactData = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get(`/travel-contacts/${id}`);
            setContact(response.data);
        } catch (error) {
            console.error('Error fetching travel contact data:', error);
            setSnackbar({ open: true, message: 'Error loading travel contact details', severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleFieldUpdate = (field, value) => {
        setUpdatedFields(prev => ({ ...prev, [field]: value }));
        setContact(prev => ({ ...prev, [field]: value }));
    };

    const handleSaveChanges = async () => {
        if (Object.keys(updatedFields).length === 0) {
            setIsEditing(false);
            return;
        }
        try {
            await axiosInstance.put(`/travel-contacts/${id}`, updatedFields);
            setUpdatedFields({});
            setIsEditing(false);
            setSnackbar({ open: true, message: 'Travel Contact updated successfully', severity: 'success' });
            fetchContactData();
        } catch (error) {
            console.error('Error updating travel contact:', error);
            setSnackbar({ open: true, message: 'Error updating travel contact', severity: 'error' });
        }
    };

    const handleDelete = async () => {
        if (window.confirm('Are you sure you want to delete this travel contact?')) {
            try {
                await axiosInstance.delete(`/travel-contacts/${id}`);
                setSnackbar({ open: true, message: 'Travel Contact deleted successfully', severity: 'success' });
                setTimeout(() => navigate('/travel-contacts'), 1000);
            } catch (error) {
                console.error('Error deleting travel contact:', error);
                setSnackbar({ open: true, message: 'Error deleting travel contact', severity: 'error' });
            }
        }
    };

    const renderSection = (title, fields) => {
      // One body for both UIs: v2 shows it in a DetailSection, legacy keeps its card.
      const sectionBody = (
        <>
            <Grid container spacing={3}>
                {fields.map((field) => (
                    <Grid item xs={12} md={6} key={field.field}>
                        <InlineEditField
                            label={field.label}
                            value={contact?.[field.field]}
                            options={field.options || null}
                            onSave={(newValue) => handleFieldUpdate(field.field, newValue)}
                            isEditing={isEditing}
                            readOnly={field.readOnly || false}
                            renderValue={field.renderValue || ((val) => (val ? String(val) : '-'))}
                        />
                    </Grid>
                ))}
            </Grid>
        </>
      );
      return (
        <UiDetailSection
          title={title}
          legacy={
            <MDBox
                mb={3}
                p={3}
                borderRadius="lg"
                sx={{
                    backgroundColor: '#ffffff',
                    boxShadow: 3
                }}
            >
                <MDTypography variant="h6" fontWeight="medium" mb={2}>{title}</MDTypography>
                {sectionBody}
            </MDBox>
          }
        >
          {sectionBody}
        </UiDetailSection>
      );
    };

    if (isLoading) {
        return (
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox p={3}>
                    <Skeleton variant="rectangular" height={400} />
                </MDBox>
                
            </DashboardLayout>
        );
    }

    if (!contact) {
        return (
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox p={3}>
                    <MDTypography>Travel Contact not found</MDTypography>
                </MDBox>
                
            </DashboardLayout>
        );
    }

    const isPassengerOrGuide = contact.record_type === 'Passenger' || contact.record_type === 'Guide';
    const isPassenger = contact.record_type === 'Passenger';

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox
                                mx={2}
                                mt={-3}
                                py={3}
                                px={2}
                                variant="gradient"
                                bgColor="info"
                                borderRadius="lg"
                                coloredShadow="info"
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <MDBox>
                                    <MDTypography variant="h5" color="white">
                                        {contact.name}
                                    </MDTypography>
                                    <MDTypography variant="button" color="white" opacity={0.8}>
                                        Travel Contact Details
                                    </MDTypography>
                                </MDBox>
                                <MDBox display="flex" gap={1}>
                                    {isEditing ? (
                                        <>
                                            <Tooltip title="Cancel">
                                                <IconButton
                                                    onClick={() => {
                                                        setIsEditing(false);
                                                        setUpdatedFields({});
                                                        fetchContactData();
                                                    }}
                                                    sx={commonIconButtonStyles}
                                                >
                                                    <Close sx={commonIconStyles} />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Save">
                                                <IconButton
                                                    onClick={handleSaveChanges}
                                                    sx={commonIconButtonStyles}
                                                >
                                                    <Save sx={commonIconStyles} />
                                                </IconButton>
                                            </Tooltip>
                                        </>
                                    ) : (
                                        <Tooltip title="Edit Travel Contact">
                                            <IconButton
                                                onClick={() => setIsEditing(true)}
                                                sx={commonIconButtonStyles}
                                            >
                                                <Edit sx={commonIconStyles} />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    <Tooltip title="Delete Travel Contact">
                                        <IconButton
                                            onClick={handleDelete}
                                            sx={commonIconButtonStyles}
                                        >
                                            <Delete sx={commonIconStyles} />
                                        </IconButton>
                                    </Tooltip>
                                </MDBox>
                            </MDBox>
                            <MDBox p={3}>
                                {renderSection("Contact Information", [
                                    { label: "Contact Name", field: "name" },
                                    { label: "Record Type", field: "record_type", options: [
                                        { value: "Passenger", label: "Passenger" },
                                        { value: "Guide", label: "Guide" },
                                        { value: "Merchant", label: "Merchant" }
                                    ]},
                                    { label: "Contact", field: "contact_id", readOnly: true, renderValue: (val) => {
                                        const c = contact.contact;
                                        return c ? `${c.first_name || ''} ${c.last_name || ''}`.trim() || c.email || val : '-';
                                    }},
                                    { label: "Account", field: "account_id", readOnly: true, renderValue: (val) => contact.account?.name || val || '-' },
                                    { label: "Email", field: "email" },
                                    { label: "Phone", field: "phone" },
                                    { label: "Primary Contact", field: "primary_contact", options: [{ value: true, label: "Yes" }, { value: false, label: "No" }], renderValue: (val) => (val ? "Yes" : "No") },
                                ])}

                                {isPassengerOrGuide && renderSection("Passport Information", [
                                    { label: "Passport Number", field: "passport_number" },
                                    { label: "Passport Issue Date", field: "passport_issue_date", readOnly: true, renderValue: (val) => val ? formatDate(val, 'date') : '-' },
                                    { label: "Passport Expiry Date", field: "passport_expiry_date", readOnly: true, renderValue: (val) => val ? formatDate(val, 'date') : '-' },
                                    { label: "Passport Issuing Country", field: "passport_issuing_country" },
                                ])}

                                {isPassenger && renderSection("Passenger Information", [
                                    { label: "Allergies", field: "allergy_condition" },
                                    { label: "Dietary Preferences", field: "dietary_preference" },
                                ])}

                                {renderSection("Address Information", [
                                    { label: "Address", field: "address" },
                                ])}

                                {renderSection("Audit Information", [
                                    { label: "Created By", field: "created_by_name", readOnly: true },
                                    { label: "Modified By", field: "updated_by_name", readOnly: true },
                                    { label: "Created Date", field: "created_at", readOnly: true, renderValue: (val) => formatDate(val, 'datetime') },
                                    { label: "Modified Date", field: "updated_at", readOnly: true, renderValue: (val) => formatDate(val, 'datetime') },
                                ])}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>
            
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </DashboardLayout>
    );
};

export default TravelContactDetail;

